/**
 * Settings-page template list: one row per crm_* template with its send stats.
 * Row click opens the page's preview drawer (media, params, delete).
 */
import React from 'react';
import { CheckCircle2, ImageOff } from 'lucide-react';
import {
  WhatsAppCloudTemplate,
  TemplateUsage,
  isMediaHeader,
  headerFormatOf,
} from '@/services/integrations/whatsappCloudService';
import { TEMPLATE_STATUS_BADGE, formatTemplateDate } from '@/utils/whatsappTemplate';

interface TemplateTableProps {
  templates: WhatsAppCloudTemplate[];
  usage: Record<string, TemplateUsage>;
  onSelect: (template: WhatsAppCloudTemplate) => void;
}

/** Naive IST from the API — parsed as local time, which is IST for every agent. */
const formatSentAt = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });

/** Count with its share of sends underneath — failures pull the rates down. */
const Rate: React.FC<{ n: number; of: number; tone: string }> = ({ n, of, tone }) => (
  <div className="text-right">
    <div className="text-gray-900 tabular-nums">{n.toLocaleString()}</div>
    {of > 0 && <div className={`text-[11px] tabular-nums ${tone}`}>{Math.round((n / of) * 100)}%</div>}
  </div>
);

const TemplateTable: React.FC<TemplateTableProps> = ({ templates, usage, onSelect }) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
          <th className="text-left font-medium px-4 py-2.5">Template</th>
          <th className="text-left font-medium px-4 py-2.5">Status</th>
          <th className="text-left font-medium px-4 py-2.5">Category</th>
          <th className="text-right font-medium px-4 py-2.5">Sent</th>
          <th className="text-right font-medium px-4 py-2.5">Delivered</th>
          <th className="text-right font-medium px-4 py-2.5">Read</th>
          <th className="text-right font-medium px-4 py-2.5">Failed</th>
          <th className="text-left font-medium px-4 py-2.5">Last sent</th>
          <th className="text-left font-medium px-4 py-2.5">Journeys</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {templates.map((t) => {
          const u = usage[t.element_name];
          const sent = u?.sent ?? 0;
          return (
            <tr
              key={t.id}
              onClick={() => onSelect(t)}
              className="cursor-pointer hover:bg-gray-50 align-top"
            >
              <td className="px-4 py-3 max-w-[320px]">
                <div className="font-medium text-gray-900 truncate">{t.element_name}</div>
                <div className="text-xs text-gray-500 truncate mt-0.5" title={t.body}>{t.body}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                  <span className="uppercase">{t.language}</span>
                  {t.parameters && t.parameters.length > 0 && (
                    <span className="text-brandColor">
                      {t.parameters.length} param{t.parameters.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {isMediaHeader(t) && (
                    t.header_media_url ? (
                      <span className="inline-flex items-center gap-1 text-green-700">
                        <CheckCircle2 className="w-3 h-3" /> {headerFormatOf(t).toLowerCase()}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <ImageOff className="w-3 h-3" /> needs {headerFormatOf(t).toLowerCase()}
                      </span>
                    )
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-0.5 text-[11px] font-medium rounded ${TEMPLATE_STATUS_BADGE[t.status] || 'bg-gray-100 text-gray-600'}`}>
                  {t.status}
                </span>
                {t.approved_at && (
                  <div className="mt-1 text-[11px] text-gray-400" title="Meta last-updated / approval time">
                    {formatTemplateDate(t.approved_at)}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-gray-600">{t.category || '—'}</td>
              <td className="px-4 py-3 text-right text-gray-900 font-medium tabular-nums">{sent.toLocaleString()}</td>
              <td className="px-4 py-3"><Rate n={u?.delivered ?? 0} of={sent} tone="text-gray-400" /></td>
              <td className="px-4 py-3"><Rate n={u?.read ?? 0} of={sent} tone="text-green-600" /></td>
              <td className="px-4 py-3">
                <Rate n={u?.failed ?? 0} of={sent} tone={(u?.failed ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'} />
              </td>
              <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                {u?.last_sent_at ? formatSentAt(u.last_sent_at) : <span className="text-gray-400">Never</span>}
              </td>
              <td className="px-4 py-3 text-xs">
                {u?.journeys?.length ? (
                  <div className="space-y-0.5 max-w-[220px]">
                    {u.journeys.map((j) => (
                      <div key={j} className="truncate text-gray-700" title={j}>{j}</div>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default TemplateTable;
